import type { ProgressTree } from '@localagent/sdk';

export interface PairableAssistantMessage {
  id: string;
  /** Slash/capability command rows are not agent-turn replies. */
  isCommand?: boolean;
}

/**
 * Map each completed reasoning run to the assistant message it produced.
 *
 * `ProgressTree.assistantMessageId` is often the session-event id (backend
 * `assistant_message` frames historically omit `message_id`), which does not
 * match REST message ids. Prefer an exact id hit when it does match a thread
 * message; otherwise pair remaining completed runs to remaining non-command
 * assistant messages in chronological order.
 */
export function pairRunsToAssistantMessages(
  runs: readonly ProgressTree[],
  assistantMessages: readonly PairableAssistantMessage[],
): Map<string, ProgressTree> {
  const map = new Map<string, ProgressTree>();
  const replyMessages = assistantMessages.filter((m) => !m.isCommand);
  const messageIds = new Set(replyMessages.map((m) => m.id));

  const completed = runs.filter((r) => r.status !== 'in_progress');
  const unmatchedRuns: ProgressTree[] = [];

  for (const run of completed) {
    if (run.assistantMessageId && messageIds.has(run.assistantMessageId)) {
      map.set(run.assistantMessageId, run);
    } else {
      unmatchedRuns.push(run);
    }
  }

  const unmatchedMessages = replyMessages.filter((m) => !map.has(m.id));
  const n = Math.min(unmatchedRuns.length, unmatchedMessages.length);
  for (let i = 0; i < n; i++) {
    map.set(unmatchedMessages[i].id, unmatchedRuns[i]);
  }

  return map;
}
